// v10/src/components/ReferenceImageModal.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const AUTO_DISMISS_DELAY = 5000;

const ReferenceImageModal = ({ item, onClose, onAutoDismiss, darkMode, autoDismiss = false }) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(autoDismiss ? Math.ceil(AUTO_DISMISS_DELAY / 1000) : null);

  useEffect(() => {
    if (!autoDismiss) return;
    const interval = setInterval(() => {
      setCountdown(prev => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; });
    }, 1000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      // onAutoDismiss permite a App.js saber que fue auto (no usuario)
      if (onAutoDismiss) onAutoDismiss();
      else if (onClose) onClose();
    }, AUTO_DISMISS_DELAY);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [autoDismiss]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!item) return null;
  const hasImage = item.referenceImage && item.referenceImage !== '';
  const title    = typeof item === 'string' ? item : (item.text || '');
  const desc     = item.description || '';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
      onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:80, background:'rgba(0,0,0,0.72)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
    >
      <motion.div
        initial={{ scale:0.92,opacity:0,y:20 }} animate={{ scale:1,opacity:1,y:0 }} exit={{ scale:0.94,opacity:0,y:10 }}
        transition={{ type:'spring',stiffness:280,damping:26 }}
        onClick={(e) => e.stopPropagation()}
        style={{ width:'100%', maxWidth:'600px', borderRadius:'24px', overflow:'hidden', background: darkMode?'rgba(10,14,26,0.98)':'rgba(255,255,255,0.98)', boxShadow:'0 32px 80px rgba(0,0,0,0.5)', border: darkMode?'1px solid rgba(255,255,255,0.07)':'1px solid rgba(0,0,0,0.08)' }}
      >
        {/* Image */}
        <div style={{ width:'100%', aspectRatio:'16/9', background:'#000', position:'relative', overflow:'hidden' }}>
          {hasImage
            ? <img src={item.referenceImage} alt={title} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
            : <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'#0a0e1a' }}><span style={{ fontSize:'64px',opacity:0.4 }}>📷</span></div>
          }

          {/* Countdown badge */}
          {autoDismiss && countdown !== null && (
            <div style={{ position:'absolute',top:'14px',left:'14px',width:'40px',height:'40px',borderRadius:'50%',background:'rgba(0,0,0,0.65)',border:'2px solid rgba(255,255,255,0.3)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',fontWeight:800,color:'white',backdropFilter:'blur(8px)' }}>
              {countdown}
            </div>
          )}

          {/* Close */}
          <motion.button onClick={onClose} whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }}
            style={{ position:'absolute',top:'14px',right:'14px',width:'36px',height:'36px',borderRadius:'50%',background:'rgba(0,0,0,0.55)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.2)',color:'white',fontSize:'18px',fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer' }}>
            ×
          </motion.button>

          {/* Progress bar */}
          {autoDismiss && (
            <div style={{ position:'absolute',bottom:0,left:0,right:0,height:'3px',background:'rgba(255,255,255,0.1)' }}>
              <motion.div initial={{ width:'100%' }} animate={{ width:'0%' }} transition={{ duration:AUTO_DISMISS_DELAY/1000,ease:'linear' }}
                style={{ height:'100%',background:'linear-gradient(90deg,#3b82f6,#7c3aed)' }} />
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ padding:'18px 20px' }}>
          <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'6px' }}>
            <div style={{ width:'30px',height:'30px',borderRadius:'9px',flexShrink:0,background:'linear-gradient(135deg,#3b82f6,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px' }}>📷</div>
            <h3 style={{ margin:0,fontSize:'17px',fontWeight:800,color: darkMode?'rgba(255,255,255,0.95)':'rgba(10,15,30,0.95)' }}>{title}</h3>
          </div>
          {desc && <p style={{ margin:'0 0 10px',fontSize:'14px',lineHeight:1.5,color: darkMode?'rgba(255,255,255,0.55)':'rgba(0,0,0,0.5)' }}>{desc}</p>}
          <p style={{ margin:0,fontSize:'11.5px',fontWeight:600,color: darkMode?'rgba(255,255,255,0.22)':'rgba(0,0,0,0.28)',textAlign:'center' }}>
            {autoDismiss ? t('modal_auto_close',{ns:'admin'}) : t('modal_close_hint',{ns:'admin'})}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReferenceImageModal;
